import { supabase } from '../lib/supabase';
import { getVisitorHash } from '../utils/visitorId';

export type FeatureSubmitResult = { ok: boolean; error?: string };

export type ForumRef = { name: string; forum_id: string };

type SupabaseErr = { code?: string; message?: string; details?: string } | null;

export function feedbackErrorMessage(err: SupabaseErr): string {
  if (!err) return 'Bilinmeyen hata';
  const msg = (err.message || '').toLowerCase();
  if (err.code === '42P01' || msg.includes('does not exist') || msg.includes('schema cache')) {
    return 'Geri bildirim tabloları henüz kurulmamış. Yönetici: Supabase’de community_feedback_bootstrap.sql çalıştırın.';
  }
  if (err.code === '42501' || msg.includes('permission denied') || msg.includes('row-level security')) {
    return 'Kayıt izni yok (RLS). Supabase politikalarını kontrol edin.';
  }
  if (err.code === '23503') return 'Forum kaydı bulunamadı.';
  return err.message || 'İşlem başarısız';
}

function isMissingTable(err: SupabaseErr): boolean {
  if (!err) return false;
  const msg = (err.message || '').toLowerCase();
  return err.code === '42P01' || msg.includes('does not exist') || msg.includes('schema cache');
}

function isUniqueViolation(err: SupabaseErr): boolean {
  return err?.code === '23505';
}

async function forumMapByIds(ids: string[]): Promise<Record<string, ForumRef>> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (!unique.length) return {};
  const { data, error } = await supabase.from('forums').select('id, name, forum_id').in('id', unique);
  if (error || !data) return {};
  const map: Record<string, ForumRef> = {};
  for (const f of data) {
    map[f.id] = { name: f.name, forum_id: f.forum_id };
  }
  return map;
}

export async function submitDataReport(forumId: string, message: string): Promise<FeatureSubmitResult> {
  const { error } = await supabase.from('data_reports').insert({
    forum_id: forumId,
    message: message.slice(0, 500),
    reporter_hash: getVisitorHash(),
    report_type: 'wrong_stats',
    status: 'pending',
  });

  if (error) return { ok: false, error: feedbackErrorMessage(error) };
  return { ok: true };
}

export async function submitPageFeedback(forumId: string, helpful: boolean): Promise<FeatureSubmitResult> {
  const visitor_hash = getVisitorHash();
  const row = { forum_id: forumId, helpful, visitor_hash };

  const { error: insertError } = await supabase.from('forum_page_feedback').insert(row);

  if (!insertError) return { ok: true };

  if (isUniqueViolation(insertError)) {
    const { error: updateError } = await supabase
      .from('forum_page_feedback')
      .update({ helpful })
      .eq('forum_id', forumId)
      .eq('visitor_hash', visitor_hash);

    if (!updateError) return { ok: true };
    return { ok: false, error: feedbackErrorMessage(updateError) };
  }

  if (isMissingTable(insertError)) {
    localStorage.setItem(`feedback_${forumId}`, helpful ? '1' : '0');
    return {
      ok: false,
      error: feedbackErrorMessage(insertError),
    };
  }

  return { ok: false, error: feedbackErrorMessage(insertError) };
}

export async function submitForumClaim(forumId: string, email: string): Promise<FeatureSubmitResult> {
  const { error } = await supabase.from('forum_claims').insert({
    forum_id: forumId,
    contact_email: email.slice(0, 120),
    status: 'pending',
  });

  if (error) return { ok: false, error: feedbackErrorMessage(error) };
  return { ok: true };
}

export type DataReportRow = {
  id: string;
  message: string | null;
  status: string;
  report_type: string;
  created_at: string;
  forum_id: string | null;
  forums: ForumRef | null;
};

export async function fetchDataReportsAdmin(): Promise<{
  data: DataReportRow[];
  error: string | null;
}> {
  const { data, error } = await supabase
    .from('data_reports')
    .select('id, message, status, report_type, created_at, forum_id')
    .order('created_at', { ascending: false })
    .limit(80);

  if (error) return { data: [], error: feedbackErrorMessage(error) };
  const rows = data || [];
  const map = await forumMapByIds(rows.map((r) => r.forum_id as string));
  return {
    data: rows.map((r) => ({
      ...r,
      forums: r.forum_id ? map[r.forum_id] ?? null : null,
    })),
    error: null,
  };
}

export type PageFeedbackRow = {
  id: string;
  helpful: boolean;
  visitor_hash: string;
  created_at: string;
  forum_id: string;
  forums: ForumRef | null;
};

export async function fetchPageFeedbackAdmin(): Promise<{
  data: PageFeedbackRow[];
  error: string | null;
}> {
  const { data, error } = await supabase
    .from('forum_page_feedback')
    .select('id, helpful, visitor_hash, created_at, forum_id')
    .order('created_at', { ascending: false })
    .limit(80);

  if (error) return { data: [], error: feedbackErrorMessage(error) };
  const rows = data || [];
  const map = await forumMapByIds(rows.map((r) => r.forum_id));
  return {
    data: rows.map((r) => ({
      ...r,
      forums: map[r.forum_id] ?? null,
    })),
    error: null,
  };
}

export type ForumClaimRow = {
  id: string;
  contact_email: string;
  status: string;
  created_at: string;
  forum_id: string;
  forums: ForumRef | null;
};

export async function fetchForumClaimsAdmin(): Promise<{
  data: ForumClaimRow[];
  error: string | null;
}> {
  const { data, error } = await supabase
    .from('forum_claims')
    .select('id, contact_email, status, created_at, forum_id')
    .order('created_at', { ascending: false })
    .limit(80);

  if (error) return { data: [], error: feedbackErrorMessage(error) };
  const rows = data || [];
  const map = await forumMapByIds(rows.map((r) => r.forum_id));
  return {
    data: rows.map((r) => ({
      ...r,
      forums: map[r.forum_id] ?? null,
    })),
    error: null,
  };
}

export async function updateDataReportStatus(id: string, status: string): Promise<FeatureSubmitResult> {
  const { error } = await supabase.from('data_reports').update({ status }).eq('id', id);
  if (error) return { ok: false, error: feedbackErrorMessage(error) };
  return { ok: true };
}

export async function updateForumClaimStatus(id: string, status: string): Promise<FeatureSubmitResult> {
  const { error } = await supabase.from('forum_claims').update({ status }).eq('id', id);
  if (error) return { ok: false, error: feedbackErrorMessage(error) };
  return { ok: true };
}
